import { useState } from "react";
import { isValidEmail, isValidMobileNumber } from "utils/Validators";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import { updateUserProfile } from "services/accountService";
import { useDispatch } from "react-redux";
import { loginUser } from "redux/reducers/authSlice";
import { toast } from "react-toastify";
import { set, setISODay } from "date-fns";

const PersonalInformationSection = ({ user }) => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        firstName: user?.name || "",
        lastName: user?.surname || "",
        displayName: user?.displayName || "",
        email: user?.email || "",
        contactNo: user?.contactNo || "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = "First name is required";
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = "Last name is required";
        }
        if (!formData.displayName.trim()) {
            newErrors.displayName = "Display name is required";
        }
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!isValidEmail(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }
        if (!isValidMobileNumber(formData.contactNo)) {
            newErrors.contactNo = "Please enter a valid 10-digit phone number";
        }
        console.log("Validation Errors:", newErrors);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage("");
        setErrorMessage("");

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const response = await updateUserProfile(formData);

            if(response && response.success){
                // Update Redux store with new user data
                dispatch(loginUser({
                    ...response.data,
                    token: localStorage.getItem('authToken') // Keep the same token
                }));
                console.log("Profile updated successfully:", response.data);
                toast.success(response?.message || "Personal information updated successfully!");
                setSuccessMessage("Personal information updated successfully!");
            }
            else{
                toast.error(response?.message || "Failed to update personal information. Please try again.");
                setErrorMessage(response?.message || "Failed to update personal information. Please try again.");
            }
        } catch (error) {
            setErrorMessage(error.message || "Failed to update personal information");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            firstName: user?.name || "",
            lastName: user?.surname || "",
            displayName: user?.displayName || "",
            email: user?.email || "",
            contactNo: user?.contactNo || "",
        });
        setErrors({});
    };

    return (
        <div className="bg-card border border-border rounded-lg p-6">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Update your personal details and contact information
                </p>
            </div>

            {successMessage && (
                <div className="mb-4 p-4 bg-success/10 border border-success text-success rounded-md">
                    ✓ {successMessage}
                </div>
            )}

            {errorMessage && (
                <div className="mb-4 p-4 bg-destructive/10 border border-destructive text-destructive rounded-md">
                    ✕ {errorMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Input
                            label="First Name"
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            error={errors.firstName}
                            required
                            placeholder="Enter your first name"
                        />
                    </div>

                    <div>
                        <Input
                            label="Last Name"
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            error={errors.lastName}
                            required
                            placeholder="Enter your last name"
                        />
                    </div>
                </div>

                <div>
                    <Input
                        label="Display Name"
                        type="text"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleChange}
                        error={errors.displayName}
                        required
                        placeholder="How you want to be displayed in the system"
                    />
                </div>

                <div>
                    <Input
                        label="Email Address"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={errors.email}
                        required
                        placeholder="your.email@example.com"
                    />
                </div>

                <div>
                    <Input
                        label="Phone Number"
                        type="tel"
                        name="contactNo"
                        value={formData.contactNo}
                        onChange={handleChange}
                        error={errors.contactNo}
                        placeholder="1234567890"
                        required
                        description="Enter 10-digit phone number without spaces or special characters"
                    />
                </div>

                <div className="flex gap-3 pt-4">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="px-6"
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleReset}
                        disabled={loading}
                    >
                        Reset
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default PersonalInformationSection;
